// Email Analytics — the email-marketing twin of PostAnalytics. Per-brand,
// with KPI cards, sent/opened/clicked/unsubscribed over time, a green→red
// per-brand engagement ranking, most-engaging emails, and most-replied /
// most-unresponsive brand cards. Mirrors PostAnalytics' shell + patterns.
// Data: GET /email-analytics/{summary,timeseries,brands,brands/history,emails/top,replies}
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setSidebarStatus } from "actions/user";
import Arrow from "assets/images/leftArr.png";
import { get } from "api/index";
import "../Dashboard/dashboard.scss";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from "recharts";

const BRAND_COLORS = ["#0F62FE", "#22c55e", "#ef4444", "#a855f7", "#f59e0b", "#06b6d4", "#ec4899", "#84cc16", "#6366f1", "#14b8a6"];

function StatCard({ label, value, accent = "#0F62FE", sub }) {
  return (
    <div className="dashboard-cards card border-0">
      <div className="card-body">
        <div style={{ fontSize: 13, color: "#64748b" }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 700, color: accent, lineHeight: 1.1, wordBreak: "break-word" }}>{value == null ? "—" : value}</div>
        {sub && <div style={{ fontSize: 12, color: "#94a3b8" }}>{sub}</div>}
      </div>
    </div>
  );
}

// green (top, 120°) → red (least, 0°) by rank — same as PostAnalytics.
function rankHue(rank, total) { return total <= 1 ? 120 : Math.round(120 * (1 - rank / (total - 1))); }

function BrandRankCard({ brand, rank, total }) {
  const hue = rankHue(rank, total);
  return (
    <div data-testid="email-brand-rank-card" data-rank={rank} style={{
      flex: "0 0 auto", minWidth: 190, borderRadius: 8, padding: "14px 16px",
      background: `hsl(${hue}, 75%, 92%)`, border: `1px solid hsl(${hue}, 60%, 70%)`, borderLeft: `4px solid hsl(${hue}, 65%, 42%)`,
    }}>
      <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>#{rank + 1}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", lineHeight: 1.15, wordBreak: "break-word" }}>{brand.name || "(unknown)"}</div>
      <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
        {brand.openRate}% open · {brand.clickRate}% click · {brand.replies} repl{brand.replies === 1 ? "y" : "ies"}
      </div>
      <div style={{ fontSize: 11, color: "#64748b" }}>score {brand.score} · {brand.sent} sent · {brand.unsubscribed} unsub</div>
    </div>
  );
}

function ChartCard({ title, children, height = 240, actions }) {
  return (
    <div className="card border-0 mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <p className="chart-header mb-0" style={{ fontSize: 14, fontWeight: 600 }}>{title}</p>
          {actions}
        </div>
        <div style={{ width: "100%", height }}>{children}</div>
      </div>
    </div>
  );
}

// GitHub-style calendar heatmap of daily email volume (out + in) — one square
// per day, greener = more email. Pure SVG (no extra dep). `series` rows are
// { date:'YYYY-MM-DD', outbound, inbound, sequenceReplies }.
function CalendarHeatmap({ series }) {
  if (!series || !series.length) return <div style={{ color: "#999", padding: 16, textAlign: "center" }}>No data</div>;
  const rows = series.map((d) => ({ ...d, v: (d.outbound || 0) + (d.inbound || 0) }));
  const byDate = new Map(rows.map((d) => [d.date, d]));
  const max = Math.max(1, ...rows.map((d) => d.v));
  const first = new Date(series[0].date + "T00:00:00Z");
  const last = new Date(series[series.length - 1].date + "T00:00:00Z");
  const start = new Date(first); start.setUTCDate(start.getUTCDate() - start.getUTCDay()); // back to Sunday
  const weeks = [];
  const cur = new Date(start);
  while (cur <= last) {
    const col = [];
    for (let dow = 0; dow < 7; dow++) {
      const key = cur.toISOString().slice(0, 10);
      col.push({ key, inRange: cur >= first && cur <= last, d: byDate.get(key) });
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    weeks.push(col);
  }
  const color = (v) => {
    if (!v) return "#ebedf0";
    const t = v / max;
    return t > 0.75 ? "#166534" : t > 0.5 ? "#22c55e" : t > 0.25 ? "#86efac" : "#dcfce7";
  };
  const CELL = 13, GAP = 3, W = CELL + GAP;
  return (
    <div style={{ overflowX: "auto", paddingTop: 4 }}>
      <svg width={weeks.length * W + 4} height={7 * W + 4} style={{ display: "block" }}>
        {weeks.map((col, wi) => col.map((cell, di) => cell.inRange ? (
          <rect key={cell.key} x={wi * W} y={di * W} width={CELL} height={CELL} rx={2}
            fill={color(cell.d ? cell.d.v : 0)}>
            <title>{`${cell.key}: ${cell.d ? cell.d.v : 0} emails (out ${cell.d ? cell.d.outbound : 0} / in ${cell.d ? cell.d.inbound : 0}${cell.d && cell.d.sequenceReplies ? `, ${cell.d.sequenceReplies} seq-reply` : ""})`}</title>
          </rect>
        ) : null))}
      </svg>
      <div className="d-flex align-items-center" style={{ gap: 4, fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
        Less
        {["#ebedf0", "#dcfce7", "#86efac", "#22c55e", "#166534"].map((c) => (
          <span key={c} style={{ width: 11, height: 11, background: c, borderRadius: 2, display: "inline-block" }} />
        ))}
        More
      </div>
    </div>
  );
}

export default function EmailAnalytics() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [brandId, setBrandId] = useState("");
  const [days, setDays] = useState(30);
  const [brandsList, setBrandsList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [history, setHistory] = useState({ brands: [], series: [] });
  const [topEmails, setTopEmails] = useState([]);
  const [replies, setReplies] = useState(null);
  const [volume, setVolume] = useState(null);

  useEffect(() => { get("/brands", false).then((r) => setBrandsList(r?.brands || r?.data || r || [])).catch(() => {}); }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const bq = brandId ? `&brand_id=${brandId}` : "";
    try {
      const [sum, ts, br, hist, top, rep, vol] = await Promise.all([
        get(`/email-analytics/summary?days=${days}${bq}`).catch(() => null),
        get(`/email-analytics/timeseries?days=${days}${bq}`).catch(() => ({ series: [] })),
        get(`/email-analytics/brands?days=${days}`).catch(() => ({ brands: [] })),
        get(`/email-analytics/brands/history?days=${days}`).catch(() => ({ brands: [], series: [] })),
        get(`/email-analytics/emails/top?${bq.slice(1)}`).catch(() => ({ emails: [] })),
        get(`/email-analytics/replies?days=${days}`).catch(() => null),
        get(`/email-analytics/volume?days=${days}${bq}`).catch(() => null),
      ]);
      setSummary(sum);
      setSeries((ts && ts.series) || []);
      setRanking((br && br.brands) || []);
      setHistory(hist || { brands: [], series: [] });
      setTopEmails((top && top.emails) || []);
      setReplies(rep);
      setVolume(vol);
    } finally { setLoading(false); }
  }, [brandId, days]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const c = summary?.contacts || {};
  const pieW = Math.max(1, (c.active || 0) + (c.inactive || 0) + (c.unsubscribed || 0));

  return (
    <div data-testid="email-analytics-page" style={{ background: "#eaeaea", minHeight: "100vh" }}>
      <div className="inner-header-position">
        <div className="custom-container d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <span style={{ alignItems: "center", display: "flex", cursor: "pointer" }} onClick={() => dispatch(setSidebarStatus(true))}>
              <div className="cursor-pointer"><img src={Arrow} className="left-arrow-img" alt="" /></div>
              <p className="my-heading f-14 ml-3 mr-3">Side Menu</p>
            </span>
            <p className="my-heading f-16 mb-0" style={{ fontWeight: 700 }}>Email Analytics</p>
          </div>
          <div className="projActionButtons d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ fontSize: 13, padding: "5px 10px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
              <option value="">All brands</option>
              {brandsList.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ fontSize: 13, padding: "5px 10px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
              <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option><option value={365}>1 year</option>
            </select>
            <button disabled={loading} onClick={loadAll} className="px-3 bg-white text-bid shadow-hover">Refresh</button>
          </div>
        </div>
      </div>

      <div className="mx-3 pt-3">
        {/* KPI row */}
        <div className="row">
          {[
            ["Sent", summary?.sent?.toLocaleString(), "#0F62FE", null],
            ["Open rate", summary != null ? `${summary.openRate}%` : null, "#22c55e", summary ? `${summary.opened?.toLocaleString()} opens` : null],
            ["Click rate", summary != null ? `${summary.clickRate}%` : null, "#06b6d4", summary ? `${summary.clicked?.toLocaleString()} clicks` : null],
            ["Reply rate", summary != null ? `${summary.replyRate}%` : null, "#a855f7", summary ? `${summary.replies?.toLocaleString()} replies` : null],
            ["Unsub rate", summary != null ? `${summary.unsubRate}%` : null, "#ef4444", summary ? `${c.unsubscribed?.toLocaleString()} unsub` : null],
            ["Bounce rate", summary != null ? `${summary.bounceRate}%` : null, "#f59e0b", summary ? `${summary.bounced?.toLocaleString()} bounced` : null],
          ].map(([label, value, accent, sub]) => (
            <div className="col-6 col-md-4 col-xl-2 mb-3" key={label}><StatCard label={label} value={value} accent={accent} sub={sub} /></div>
          ))}
        </div>

        {/* Contacts split */}
        <div className="row">
          <div className="col-md-4 mb-3"><StatCard label="Active contacts" value={c.active?.toLocaleString()} accent="#22c55e" sub={`${Math.round(((c.active || 0) / pieW) * 100)}% engaged (30d)`} /></div>
          <div className="col-md-4 mb-3"><StatCard label="Inactive contacts" value={c.inactive?.toLocaleString()} accent="#64748b" sub="mailed, no open/click" /></div>
          <div className="col-md-4 mb-3"><StatCard label="Unsubscribed" value={c.unsubscribed?.toLocaleString()} accent="#ef4444" sub={`of ${c.total?.toLocaleString() || 0} total`} /></div>
        </div>

        {/* Sent/open/click/unsub over time */}
        <ChartCard title="Sent · Opened · Clicked · Unsubscribed over time">
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sent" stroke="#0F62FE" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="opened" stroke="#22c55e" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="clicked" stroke="#06b6d4" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="unsubscribed" stroke="#ef4444" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* STAT 1 — emails sent (out) vs received (in) per day */}
        <ChartCard title="Emails sent vs received per day"
          actions={volume?.totals && (
            <span style={{ fontSize: 12, color: "#64748b" }}>
              {(volume.totals.outbound || 0).toLocaleString()} sent · {(volume.totals.inbound || 0).toLocaleString()} received · {(volume.totals.sequenceReplies || 0).toLocaleString()} sequence replies
            </span>
          )}>
          <ResponsiveContainer>
            <BarChart data={volume?.series || []} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="outbound" name="Sent (out)" fill="#0F62FE" />
              <Bar dataKey="inbound" name="Received (in)" fill="#22c55e" />
              <Bar dataKey="sequenceReplies" name="Sequence replies" fill="#a855f7" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* STAT 2 — calendar heatmap of daily email volume */}
        <ChartCard title="Email activity calendar (sent + received per day)" height={150}>
          <CalendarHeatmap series={volume?.series || []} />
        </ChartCard>

        {/* STAT 3 — inbound split: sequence replies vs unrelated, per brand */}
        <ChartCard title="Inbound replies per brand: sequence replies vs unrelated">
          <ResponsiveContainer>
            <BarChart data={volume?.byBrand || []} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="brandName" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={54} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="sequenceReplies" name="Reply to a sequence" stackId="in" fill="#a855f7" />
              <Bar dataKey="unrelatedInbound" name="Unrelated inbound" stackId="in" fill="#94a3b8" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Per-brand ranking gradient strip */}
        <div className="card border-0 mb-3"><div className="card-body">
          <p className="chart-header mb-2" style={{ fontSize: 14, fontWeight: 600 }}>Brands by engagement (green = top, red = least)</p>
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
            {ranking.length === 0 ? <div style={{ color: "#94a3b8", fontSize: 13 }}>No email engagement yet.</div>
              : ranking.map((b, i) => <BrandRankCard key={b.brandId} brand={b} rank={i} total={ranking.length} />)}
          </div>
        </div></div>

        {/* Most replied / most unresponsive */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="card border-0" style={{ borderLeft: "4px solid #22c55e" }}><div className="card-body">
              <div style={{ fontSize: 13, color: "#64748b" }}>Most replied-to brand</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>{replies?.mostReplied?.name || "—"}</div>
              {replies?.mostReplied && <div style={{ fontSize: 12, color: "#475569" }}>{replies.mostReplied.replies} replies · {replies.mostReplied.replyRate}% reply rate</div>}
            </div></div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="card border-0" style={{ borderLeft: "4px solid #ef4444" }}><div className="card-body">
              <div style={{ fontSize: 13, color: "#64748b" }}>Most unresponsive brand</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>{replies?.mostUnresponsive?.name || "—"}</div>
              {replies?.mostUnresponsive && <div style={{ fontSize: 12, color: "#475569" }}>{replies.mostUnresponsive.replyRate}% reply rate · {replies.mostUnresponsive.sent} sent</div>}
            </div></div>
          </div>
        </div>

        {/* Per-brand engagement over time */}
        <ChartCard title="Per-brand engagement over time (opens + 3×clicks)">
          <ResponsiveContainer>
            <LineChart data={history.series} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={24} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              {(history.brands || []).slice(0, 10).map((bn, i) => (
                <Line key={bn} type="monotone" dataKey={bn} stroke={BRAND_COLORS[i % BRAND_COLORS.length]} dot={false} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Most engaging emails */}
        <div className="card border-0 mb-4"><div className="card-body">
          <p className="chart-header mb-3" style={{ fontSize: 14, fontWeight: 600 }}>Most engaging emails</p>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            <table className="table" style={{ fontSize: 13 }}>
              <thead><tr><th>Subject</th><th>Brand</th><th>Kind</th><th>Opened</th><th>Clicked</th></tr></thead>
              <tbody>
                {topEmails.length === 0 ? <tr><td colSpan={5} style={{ color: "#94a3b8" }}>No engaged emails yet.</td></tr>
                  : topEmails.map((e, i) => (
                    <tr key={i}>
                      <td style={{ maxWidth: 380, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.subject}</td>
                      <td>{e.brand}</td><td><span style={{ fontSize: 11, color: "#64748b" }}>{e.kind}</span></td>
                      <td>{e.opened ? "✓" : "—"}</td>
                      <td style={{ color: e.clicked ? "#06b6d4" : "#cbd5e1", fontWeight: e.clicked ? 700 : 400 }}>{e.clicked ? "✓" : "—"}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div></div>
      </div>
    </div>
  );
}
